import React, { useState, useRef } from 'react';
import TagInput from '../components/TagInput';

function parseCards(text) {
  return text.split(/\n\s*\n/).map(s => s.trim()).filter(Boolean);
}

function parseCSV(text) {
  const lines = text.trim().split('\n');
  if (lines.length === 0) return [];
  
  // Check if first line is a header
  const firstLine = lines[0].toLowerCase();
  const hasHeader = firstLine.includes('body') || firstLine.includes('text') || firstLine.includes('card');
  const dataLines = hasHeader ? lines.slice(1) : lines;
  
  const cards = [];
  for (const line of dataLines) {
    if (!line.trim()) continue;
    // Parse CSV line handling quoted fields
    const fields = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') {
        inQuotes = !inQuotes;
      } else if (ch === ',' && !inQuotes) {
        fields.push(current.trim());
        current = '';
      } else {
        current += ch;
      }
    }
    fields.push(current.trim());
    
    const body = fields[0]?.trim();
    const tagField = fields[1]?.trim() || '';
    const tags = tagField ? tagField.split(',').map(t => t.trim().toLowerCase()).filter(Boolean) : [];
    
    if (body) cards.push({ body, tags });
  }
  return cards;
}

export default function BulkEntry() {
  const [mode, setMode] = useState('text'); // 'text' or 'csv'
  const [text, setText] = useState('');
  const [tags, setTags] = useState([]);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState('');
  const tagInputRef = useRef();

  const isCSV = mode === 'csv';
  const textPreview = isCSV ? parseCSV(text) : parseCards(text);
  const preview = textPreview;

  const handleSubmit = async () => {
    if (preview.length === 0) { setError('No cards found.'); return; }
    setSaving(true);
    setError('');
    try {
      let res;
      if (isCSV) {
        // Send each card with its own tags
        const cards = parseCSV(text);
        res = await fetch('/api/cards/bulk-csv', {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ cards })
        });
      } else {
        const finalTags = tagInputRef.current ? tagInputRef.current.flush() : tags;
        res = await fetch('/api/cards/bulk', {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ text, tags: finalTags })
        });
      }
      const data = await res.json();
      if (res.ok) {
        setSuccess(data.created);
        setText('');
        setTags([]);
        setMode('text');
      } else {
        setError(data.error || 'Failed to import.');
      }
    } catch {
      setError('Connection error. Try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ maxWidth: 700 }}>
      <div className="page-header">
        <h1>Bulk Entry</h1>
        <div className="subtitle">Paste, type, or upload multiple cards at once.</div>
      </div>

      {success !== null && (
        <div style={{ background: 'var(--accent)', color: 'white', padding: '0.75rem 1rem', borderRadius: 'var(--radius)', marginBottom: '1.5rem', fontFamily: 'sans-serif' }}>
          {success} card{success !== 1 ? 's' : ''} added to the box.{' '}
          <button onClick={() => setSuccess(null)} style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer', textDecoration: 'underline', fontFamily: 'sans-serif' }}>
            Add more
          </button>
        </div>
      )}

      {/* Mode tabs */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
        <button
          className={`btn ${mode === 'text' ? 'btn-primary' : 'btn-secondary'}`}
          style={{ fontSize: '0.875rem' }}
          onClick={() => { setMode('text'); setText(''); setError(''); }}
        >Plain text</button>
        <button
          className={`btn ${mode === 'csv' ? 'btn-primary' : 'btn-secondary'}`}
          style={{ fontSize: '0.875rem' }}
          onClick={() => { setMode('csv'); setText(''); setError(''); }}
        >CSV (with tags)</button>
      </div>

      <div className="form-group">
        <div style={{ marginBottom: '0.4rem' }}>
          <label className="form-label" style={{ margin: 0 }}>
            {isCSV ? 'CSV content' : 'Cards'}
          </label>
        </div>
        <textarea
          className="form-textarea"
          value={text}
          onChange={e => { setText(e.target.value); setError(''); }}
          placeholder={isCSV
            ? `body,tags\nShe left her umbrella on the bus.,situation\nThe smell of a hardware store.,"object,location"\n"I don't want to be right. I want to be done.",dialogue`
            : `She left her umbrella on the bus and didn't notice until she was already soaked.\n\nThe smell of a hardware store — wood shavings and motor oil.\n\n"I don't want to be right. I want to be done."`
          }
          rows={10}
        />
        <div style={{ fontFamily: 'sans-serif', fontSize: '0.82rem', color: 'var(--text-muted)', marginTop: '0.4rem' }}>
          {isCSV
            ? 'Format: body,tags — multiple tags separated by commas inside quotes'
            : 'One blank line between cards. Each block of text becomes one card.'}
        </div>
      </div>

      {text.trim() && (
        <div className="bulk-preview">
          <strong>{preview.length} card{preview.length !== 1 ? 's' : ''} detected</strong>
          {preview.slice(0, 3).map((p, i) => (
            <div key={i} style={{ marginTop: '0.5rem', paddingTop: '0.5rem', borderTop: '1px solid var(--border)', fontSize: '0.85rem', color: 'var(--text)' }}>
              {isCSV ? (
                <span>
                  {p.body.length > 100 ? p.body.slice(0, 100) + '…' : p.body}
                  {p.tags.length > 0 && <span style={{ color: 'var(--accent)', marginLeft: '0.5rem' }}>[{p.tags.join(', ')}]</span>}
                </span>
              ) : (
                <span style={{ whiteSpace: 'pre-wrap' }}>{typeof p === 'string' && p.length > 120 ? p.slice(0, 120) + '…' : p}</span>
              )}
            </div>
          ))}
          {preview.length > 3 && (
            <div style={{ marginTop: '0.5rem', color: 'var(--text-muted)', fontSize: '0.82rem' }}>
              + {preview.length - 3} more
            </div>
          )}
        </div>
      )}

      {!isCSV && (
        <div className="form-group" style={{ marginTop: '1rem' }}>
          <label className="form-label">Apply tags to all (optional)</label>
          <TagInput ref={tagInputRef} value={tags} onChange={setTags} />
        </div>
      )}

      {error && <p style={{ color: 'var(--no)', fontFamily: 'sans-serif', fontSize: '0.9rem', marginBottom: '0.75rem' }}>{error}</p>}

      <button
        className="btn btn-primary"
        onClick={handleSubmit}
        disabled={saving || preview.length === 0}
        style={{ fontSize: '1rem' }}
      >
        {saving ? 'Importing...' : `Add ${preview.length > 0 ? preview.length : ''} card${preview.length !== 1 ? 's' : ''} to box`}
      </button>
    </div>
  );
}
